import { AppConstants } from '@unpispas/upp-defs';
import { dataService } from '../../data';

import { DataObject, ObjectOptions, RelatedObject } from "../base";
import { CreateObject } from "../item";
import { Place } from './place';
import { ProductView, ProductViewProxy } from '../views/product';

import { Family } from './family';
import { ProductPrice } from './productprice';
import { PriceZone } from './pricezone';
import { PriceTime } from './pricetime';

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

import { PlaceType } from '../types';
import { PreselectType } from '../types';
import { ProductType } from '../types';
import { FamilyType } from '../types';
import { CategoryType } from '../types';
import { ProductPriceType } from '../types';
import { I18nValueType } from '../types';

const _schema = {
    "name": "PRODUCT",
    "fields": [
        { name: "status", type: "string", default: "AC" },
        { name: "place", type: "objid" },
        { name: "parent", type: "objid" },
        { name: "next", type: "objid" },
        { name: "sort", type: "number" },
        { name: "name", type: "objid" },
        { name: "ctgref", type: "string" },
        { name: "type", type: "string" },
        { name: "code", type: "string" },
        { name: "barcode", type: "string" },
        { name: "alias", type: "string" },
        { name: "photo", type: "file" },
        { name: "description", type: "objid" },
        { name: "isgroup", type: "boolean" },
        { name: "generic", type: "boolean" },
        { name: "isfamily", type: "boolean" },
        { name: "price", type: "number" },
        { name: "taxrate", type: "number" },
        { name: "flags", type: "string" }
    ],
    relate: [
        { direction: ">", target: "PLACE", by: "place", name: "place", reason: "place", child: false, class: PlaceType },
        { direction: ">", target: "I18NVALUE", by: "name", name: "name", reason: "name", child: true, class: I18nValueType },
        { direction: ">", target: "I18NVALUE", by: "description", name: "description", reason: "description", child: true, class: I18nValueType },
        { direction: ">", target: "PRODUCT", by: "parent", name: "parent", reason: "parent", child: true, class: ProductType },
        { direction: ">", target: "PRODUCT", by: "next", name: "next", reason: "next", child: false, class: ProductType },
        { direction: "<", target: "CATEGORY", by: "product", name: "category", reason: "categories", child: true, class: CategoryType },
        { direction: "<", target: "PRESELECT", by: "product", name: "select", reason: "selects", child: true, class: PreselectType },
        { direction: "<", target: "FAMILY", by: "product", name: "family", reason: "families", child: true, class: FamilyType },
        { direction: "<", target: "PRODUCTPRICE", by: "product", name: "price", reason: "prices", child: true, class: ProductPriceType }
    ]
} as const;

const _ProductClass = CreateObject(_schema);
if (!_ProductClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* CLASS DECLARATION        */
/****************************/

enum ProductFlags {
    HideApp = 0,         // Hide product on QR APP access 
    HidePdf = 1,         // Hide product on PDF menu
    HideWeb = 2,         // Hide product on WEB access
    MarketPrice = 3,     // Does not have a fixed price
    Weighted = 4         // This product has to be weighted
};

export class Product extends _ProductClass { 
    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null){
        super(_schema, objid, data, objoptions || {});

        if (!this._data['flags']){  // initialize the flags value (cannot be null)
            this._data['flags'] = '0'.repeat(Object.keys(ProductFlags).length / 2);
        }
    }

    Copy(store: Array<DataObject> = []): Product {
        return this._Copy(store) as Product;
    }

    /** Changelog rows lack parent 1:N lists; register on link (see Preselect / TicketProduct). */
    protected override DoChildLinked(relation: string | null, related: RelatedObject | null): void {
        if (this.CopyOf || !this.Commited){
            return;
        }

        if (relation === 'place' && related instanceof Place){
            if (related === this.place){
                related.AddProduct(this);
            }
        }
    }

    override get viewProxy(): ProductView {
        return ProductViewProxy(this, this.data);
    }

    /****************************/
    /* FLAGS MANAGEMENT         */
    /****************************/

    protected get flagArray(): Array<boolean> {
        const _array: boolean[] = new Array(Object.keys(ProductFlags).length / 2).fill(false);
        
        if (this.flags){
            const _flags = Array.from(this.flags);
            for(let i=0; i < _flags.length; i++){
                _array[i] = (_flags[i] == '1');
            }        
        }

        return _array;
    }

    protected set flagArray(value: Array<boolean>) {
        const _array: string[] = [];
        for(let i=0; i < value.length; i++){
            _array[i] = (value[i] === true) ? '1' : '0';
        }

        this.flags = _array.join(''); 
    }

    protected _setFlagValue(flag: ProductFlags, value: boolean){
        const _flags = this.flagArray;
        _flags[flag] = value;
        this.flagArray = _flags;        
    }

    get HideApp() : boolean {
        return this.flagArray[ProductFlags.HideApp];
    }

    set HideApp(value: boolean){
        this._setFlagValue(ProductFlags.HideApp, value);
    }

    get HidePdf() : boolean {
        return this.flagArray[ProductFlags.HidePdf];
    }

    set HidePdf(value: boolean){
        this._setFlagValue(ProductFlags.HidePdf, value);
    }

    get HideWeb() : boolean {
        return this.flagArray[ProductFlags.HideWeb];
    }

    set HideWeb(value: boolean){
        this._setFlagValue(ProductFlags.HideWeb, value);
    }

    get IsMarket() : boolean {
        return this.flagArray[ProductFlags.MarketPrice];
    }

    set IsMarket(value: boolean) {
        this._setFlagValue(ProductFlags.MarketPrice, value);        
    }

    get IsWeighted() : boolean {
        return this.flagArray[ProductFlags.Weighted];
    }

    set IsWeighted(value: boolean ) {
        this._setFlagValue(ProductFlags.Weighted, value);        
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    get avatar(): string | null {
        const _bg = 'd3c79b';
        const _fg = '000000';
        const _lb = this.name?.tr() ?? null;

        if (!_lb){
            return null;
        }

        return AppConstants.baseURL + 'photo/avatar?size=128&background=' + _bg + '&color=' + _fg + '&name=' + encodeURIComponent(_lb);
    }

    get photo(): string | null {
        return this['__base__photo'] || this.avatar;
    }

    set photo(value: string | null){
        this['__base__photo'] = value;
    }

    /** True when a real photo is stored (excludes the generated avatar). */
    get HasPhoto(): boolean {
        return !!this['__base__photo'];
    }

    get status(): string | null {
        return this['__base__status'];
    }

    set status(value: string){
        if (this.status == 'DE'){
            return;     // cannot modify deleted items
        }

        if (this.status == value){
            return;
        }

        // remove catalog consideration when status is updated
        if (!['DE', 'CT'].includes(value)){
            this.IsCatalog = false;
        }

        this['__base__status'] = value;
        if (this.place){
            if ((this.status == 'DE') && !this.Commited){
                this.place.DelProduct(this);
            }
            else {
                this.place.DoRefresh(this.table);
            }    
        }
    }

    get taxrate(): number {
        return this['__base__taxrate'] ?? AppConstants.defaultTaxRate;
    }

    set taxrate(value: number | null){
        this['__base__taxrate'] = value;
    }

    get family(): Family | null {
        return this.families.find(_family => _family.IsValid) || null;        
    }

    set family(value: Family | null) {
        if (this.family != value){
            if (this.family){
                this.family.status = 'DE';
            }

            if (value != null){
                this.AddFamily(value);
            }

            this.isfamily = (this.family != null);
        }        
    }

    /****************************/
    /* CATALOG CONSIDERATIONS   */
    /****************************/

    override get IsCatalog(): boolean {
        return super.IsCatalog;
    }

    override set IsCatalog(value: boolean){
        if (value == this.IsCatalog){
            return;
        }

        super.IsCatalog = value;
        if (value == false){
            if (this.status == 'CT'){
                this.status = 'AC';
            }

            for (const _child of this.Children){
                _child.IsCatalog = value;
            }
        }
    }

    /****************************/
    /* REPLACEMENT CHAIN        */
    /****************************/

    /**
     * Follows the `next` replacement chain to the definitive place product.
     * Catalog templates link forward to the active product that replaced them.
     */
    get last(): Product {
        if (!this.next){
            return this;
        }

        return (this.next as Product).last;
    }

    override get parent(): Product | null {
        const _raw = (this._children['parent'] as Product) || null;
        if (!_raw){
            return null;
        }

        const _resolved = _raw.last;
        return _resolved?.IsValid ? _resolved : null;
    }

    override set parent(value: Product | null){
        const _resolved = value ? value.last : null;
        if (this.SetChild('parent', _resolved, 'parent')){
            this.ToUpdate = true;
        }
    }

    /****************************/
    /* CUSTOM METHODS           */
    /****************************/

    get available(): boolean {
        return (this.status == 'AC') || (this.status == 'OT');
    }

    set available(value: boolean){
        this.status = (value) ? 'AC' : 'UN';
    }

    get IsValid(): boolean {
        return (!!this.status && (this.status != 'DE'));
    }

    /**
     * Whether this product is covered by a catalog entry: the same logical row
     * (via `last`), or a descendant when that entry is a group.
     */
    IsChild(entry: Product | null): boolean {
        if (entry == null){
            return false;
        }

        const _entry = entry.last;
        if (!_entry.IsValid){
            return false;
        }

        if (this.last === _entry){
            return true;
        }

        if (!_entry.isgroup){
            return false;
        }

        let _parent = this.parent;
        while (_parent != null){
            if (_parent === _entry){
                return true;
            }

            _parent = _parent.parent;
        }

        return false;
    }

    /****************************/
    /* ZONE/PERIOD PRICE        */
    /****************************/

    /**
     * Applicable price for `zone` + `time`: exact override when present, else the custom
     * zone default (`time == null`) when asking for a time slot on a custom zone, else
     * the base `price` field. Both `null` is the base slot (never a `PRODUCTPRICE` row).
     */
    GetPriceAt(zone: PriceZone | null, time: PriceTime | null): number | null {
        if (zone === null && time === null){
            return this.price ?? null;
        }

        for (const _row of this.prices){
            if (_row.IsValid && (_row.pricezone === zone) && (_row.pricetime === time)){
                const _exact = _row.price ?? null;
                if (_exact !== null){
                    return _exact;
                }
                break;
            }
        }

        if (zone !== null && time !== null){
            for (const _row of this.prices){
                if (_row.IsValid && (_row.pricezone === zone) && (_row.pricetime === null)){
                    const _zoneDefault = _row.price ?? null;
                    if (_zoneDefault !== null){
                        return _zoneDefault;
                    }
                    break;
                }
            }
        }

        return this.price ?? null;
    }

    /**
     * Writes the price for `zone` + `time`. Both `null` writes the base `price` field
     * directly (ignored when `price` is `null`, since the base price cannot be cleared this
     * way); otherwise creates, updates or clears the `PRODUCTPRICE` override row for that slot,
     * without duplicating rows for the same slot (`price === null` deletes the row if present).
     */
    SetPriceAt(zone: PriceZone | null, time: PriceTime | null, price: number | null): void {
        if (zone === null && time === null){
            if (price !== null){
                this.price = price;
            }
            return;
        }

        let _existing: ProductPrice | null = null;
        for (const _row of this.prices){
            if (_row.pricezone === zone && _row.pricetime === time){
                _existing = _row;
                break;
            }
        }

        if (price === null){
            if (_existing){
                _existing.status = 'DE';
            }
            return;
        }

        if (_existing){
            _existing.price = price;
            if (!_existing.IsValid){
                _existing.status = 'AC';
            }
            _existing.ToUpdate = true;
            return;
        }

        const _row = new ProductPrice(null, this.data);
        _row.product = this;
        _row.pricezone = zone;
        _row.pricetime = time;
        _row.price = price;
        _row.status = 'AC';
        this.AddPrice(_row);
    }
}


